import * as React from 'react';
import { cn } from '../lib/utils';

export type SegmentedCategory = {
  /** Name of the category (displayed in legend) */
  name: string;
  /** Percentage value (0-100) */
  value: number;
  /** Absolute amount (e.g., number of materials) */
  amount: number;
  /** CSS color value or variable (e.g., 'var(--color-success)') */
  color: string;
};

export type SegmentedProgressBarProps = {
  /** Title displayed above the bar */
  title: string;
  /** Array of categories with name, value, and color */
  categories: SegmentedCategory[];
  /** Overall percentage value and classification */
  overall?: { label: string; value: number };
  /** Additional CSS class for the container */
  className?: string;
};

/**
 * Segmented progress bar component following Shelflix Admin design system.
 *
 * Displays a horizontal bar divided into colored segments based on category percentages,
 * with a legend below showing each category's color, name, and value.
 *
 * Features:
 * - Title: 20px, weight 400, --color-neutral-300
 * - Bar height: 16px, full width, rounded (pill-shaped)
 * - Legend color indicator: 24x24px circle
 * - Category name: 18px, weight 400, --color-neutral-300
 * - Category value: 18px, weight 400, category color
 */
const SegmentedProgressBar = React.forwardRef<HTMLDivElement, SegmentedProgressBarProps>(
  ({ title, categories, overall, className }, ref) => {
    // Calculate total to normalize values if they don't sum to 100
    const total = categories.reduce((sum, cat) => sum + cat.value, 0);

    return (
      <div ref={ref} className={cn('flex w-full flex-col gap-4', className)}>
        {/* Header */}
        <div className="flex w-full flex-col gap-2">
          {/* Title */}
          <div className="flex h-8.5 items-center justify-between text-[var(--color-neutral-200)]">
            <span className="text-body align-middle">{title}</span>
          </div>

          {/* Segmented bar */}
          <div className="flex h-4 w-full overflow-hidden rounded-[50px] border border-[var(--color-neutral-900)] bg-[var(--color-neutral-950)]">
            {categories.map((category, index) => {
              // Calculate width as percentage of total
              const widthPercent = total > 0 ? (category.value / total) * 100 : 0;

              return (
                <div
                  key={category.name}
                  className={cn(
                    'h-full transition-all duration-300 ease-out',
                    // First segment: rounded left corners
                    index === 0 && 'rounded-l-[50px]',
                    // Last segment: rounded right corners
                    index === categories.length - 1 && 'rounded-r-[50px]'
                  )}
                  style={{
                    width: `${widthPercent}%`,
                    backgroundColor: category.color,
                  }}
                />
              );
            })}
          </div>

          <div className="flex h-8.5 items-center justify-between text-[var(--color-neutral-100)]">
            <span className="text-body">Geral: {overall?.label}</span>
            <span className="text-body">{overall?.value}%</span>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-2 flex flex-col gap-4">
          {categories.map((category) => (
            <div key={category.name} className="flex items-center justify-between">
              {/* Left side: color circle + category name */}
              <div className="flex items-center gap-3">
                <div className="m-[5px] h-6 w-6 shrink-0 rounded-full" style={{ backgroundColor: category.color }} />
                <h4 className="text-h4 text-[var(--color-neutral-200)]">{category.name}</h4>
              </div>

              {/* Right side: percentage value */}
              <div className="flex items-center">
                <span className="text-caption text-[var(--color-neutral-200)]">{category.amount} materiais</span>
                <span className="text-label w-15 text-end" style={{ color: category.color }}>
                  {category.value}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

SegmentedProgressBar.displayName = 'SegmentedProgressBar';

export { SegmentedProgressBar };
